import { Component, lazy, Suspense, type ReactNode } from "react";

import { useAccount, type CallingFlags } from "@/providers/AccountProvider";

// Where AppShell mounts the call dock -- once, outside <main> and outside the routed page's
// boundary (docs/vault/Plans/WhatsApp-Calling.md §11).
//
// The dock is its own chunk, so a workspace without calling never downloads the softphone. That
// chunk gets its OWN Suspense boundary on purpose: AppShell has none, and the nearest one is
// App.tsx's, around every route, whose fallback is the whole-app skeleton. Without this one, the
// dock chunk suspending on an ordinary update -- the gate turning true after identity loads --
// would swap the entire app, page included, for that skeleton.

const CallDock = lazy(() => import("@/calls/CallDock"));

/** §10: only where calling is live, to a real seat that may answer. The server checks again. */
export function dockAllowed(calling: CallingFlags, can: (permission: string) => boolean): boolean {
  return calling.platform && calling.enabled && calling.seat && can("calls.answer");
}

interface DockErrorBoundaryState {
  failed: boolean;
}

/** A dock that crashes costs the dock, never the page or the shell. */
class DockErrorBoundary extends Component<{ children: ReactNode }, DockErrorBoundaryState> {
  override state: DockErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): DockErrorBoundaryState {
    return { failed: true };
  }

  override render() {
    if (!this.state.failed) return this.props.children;
    return (
      <p
        role="alert"
        className="fixed bottom-4 right-4 z-40 max-w-[calc(100vw-2rem)] rounded-[var(--radius-ctl)] border-hairline border-brand-border bg-brand-surface px-3 py-2 text-[12px] text-n-fg-muted"
      >
        Calls stopped working. Reload the page to take calls again.
      </p>
    );
  }
}

export function CallDockRoot() {
  const { calling, can } = useAccount();
  if (!dockAllowed(calling, can)) return null;
  return (
    <DockErrorBoundary>
      <Suspense fallback={null}>
        <CallDock />
      </Suspense>
    </DockErrorBoundary>
  );
}
